const parse = require("html-react-parser");
const domToReact = parse.domToReact || require("html-react-parser").domToReact;
const React = require("react");

function getTokenDetails(token) {
  if (!token) {
    return "";
  } else {
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace("-", "+").replace("_", "/");
    return JSON.parse(window.atob(base64));
  }
}

/**
 * Safely renders HTML content into React components.
 * @param {string} htmlString - The HTML string to render.
 * @returns {React.ReactNode} - The rendered React elements.
 */
const renderHtmlContent = (htmlString) => {
  if (!htmlString) return null;
  // Options can be added here to modify/replace elements if needed
  return parse(htmlString);
};

/**
 * Renders HTML content for preview cards (Home Feed).
 * Flattens block elements (p, div) to inline elements to support CSS line-clamp.
 * Preserves inline formatting (bold, italic, etc.).
 * Removes empty paragraphs/breaks to densify the text.
 * @param {string} htmlString - The HTML content.
 * @param {boolean} preserveLineBreaks - If true, inserts <br> instead of space between blocks (useful for poems).
 */
const renderPreviewHtml = (htmlString, preserveLineBreaks = false) => {
  if (!htmlString) return null;

  const options = {
    replace: (domNode) => {
      // Check if it's an element tag
      if (domNode.type === "tag") {
        // 1. Handle Paragraphs and Divs
        if (domNode.name === "p" || domNode.name === "div") {
          // Check if it's empty or just has a break (sparse content)
          const hasContent = domNode.children.some((child) => {
            return (
              (child.type === "text" && child.data.trim().length > 0) ||
              (child.type === "tag" && child.name !== "br")
            );
          });

          if (!hasContent) {
            return React.createElement(React.Fragment, null); // Remove empty blocks
          }

          // Render contents inline
          return React.createElement(
            React.Fragment,
            null,
            domToReact(domNode.children, options),
            preserveLineBreaks
              ? React.createElement("br")
              : " "
          );
        }

        // 2. Handle explicit line breaks <br>
        if (domNode.name === "br") {
          // If preserving line breaks, keep the break.
          // Otherwise, replace with space for density.
          return preserveLineBreaks
            ? React.createElement("br")
            : React.createElement("span", { className: "preview-spacer" }, " ");
        }
      }
    },
  };

  return parse(htmlString, options);
};

/**
 * Strips HTML tags from a string to return plain text.
 * Useful for creating plain text previews.
 * @param {string} html - The HTML string to strip.
 * @returns {string} - The plain text content.
 */
const stripHtml = (html) => {
  if (!html) return "";
  // Create a temporary DOM element to let the browser handle parsing
  const tmp = document.createElement("DIV");
  tmp.innerHTML = html;

  // Helper to handle line breaks better than default textContent
  let text = tmp.innerHTML;
  text = text.replace(/<br\s*\/?>/gi, "\n"); // Replace <br> with newline
  text = text.replace(/<\/p>/gi, "\n"); // Replace </p> with newline
  text = text.replace(/<[^>]+>/g, ""); // Strip remaining tags
  text = text.replace(/&nbsp;/g, " "); // Replace &nbsp; with space

  // Decode other entities if needed
  const textarea = document.createElement("textarea");
  textarea.innerHTML = text;
  text = textarea.value;

  return text.trim();
};

module.exports = {
  getTokenDetails: getTokenDetails,
  renderHtmlContent: renderHtmlContent,
  renderPreviewHtml: renderPreviewHtml,
  stripHtml: stripHtml,
};
